import React, { useEffect, useState, useRef } from 'react';
import SideBar from '../components/SideBar';
import DetailsHeaderBar from '../components/DetailsHeaderBar';
import DetailsTopBar from '../components/DetailsTopBar';
import { useLocation, useNavigate } from 'react-router-dom';
import HistoryTable from '../components/HistoryTable';
import { agentService } from '../services/AgentServices';

function AgentInvitationHistory() {
  const location = useLocation();
  const agent = location.state?.agent;
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState('');
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  useEffect(() => {
    if (agent?.agent_id) {
      fetchReferralHistory();
    } else {
      // If no agent, stop loading after minimum time
      setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [agent, currentPage]);

  const fetchReferralHistory = async () => {
    if (!agent?.agent_id) {
      setError('No agent ID available');
      setTimeout(() => {
        setLoading(false);
      }, 3000);
      return;
    }

    setLoading(true);
    setError('');
    
    // Record start time for minimum loading duration
    const startTime = Date.now();
    const MIN_LOADING_TIME = 3000; // 3 seconds

    try {
      const filters = {
        page: currentPage,
        per_page: 10
      };

      const response = await agentService.getAgentReferralHistory(agent.agent_id, filters);
      console.log('Using Agent ID:', agent.agent_id);
      if (response.referrals) {
        setInvitations(response.referrals);
        setHasDataLoaded(true);
      }
    } catch (err) {
      setError('Failed to fetch referral history');
      console.error('Error fetching referral history:', err);
    } finally {
      // Calculate remaining time to reach minimum loading duration
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      // Wait for remaining time before setting loading to false
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }
  };

  if (!agent) {
    return <p style={{ padding: "30px" }}>No agent data available.</p>;
  }

  // Define all possible tabs
  const allTabs = [
    {
      key: "personal",
      label: "Personal Details",
      path: "/agent-details",
    },
    {
      key: "wallet",
      label: "Wallet Details",
      path: "/agent-wallet-details",
    },
    {
      key: "invitation",
      label: "Invitation Details",
      path: "/agent-invite-details",
      showWhen: "approved",
    },
    {
      key: "request",
      label: "Promote / Demote",
      path: "/agent-promotion",
    },
  ];

  // Filter tabs based on status
  const getTabsByStatus = allTabs;

  const tabs = allTabs;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Prepare data for the table - using the same structure as before
  const tableData = invitations.map(invitation => ({
    name: invitation.name || `${invitation.first_name || ''} ${invitation.last_name || ''}`.trim(),
    email: invitation.email || 'N/A',type: invitation.type || 'N/A',
    joinedDate: formatDate(invitation.joined_date),
    status: invitation.status || 'pending'
  }));

  // Table column configuration - same as before
  const columns = [
    { label: "Name", field: "name" },
    { label: "Email", field: "email" },
    { label: "Type", field: "type" },
    { label: "Joined Date", field: "joinedDate" },
    { label: "Status", field: "status" }
  ];

  const goToEdit = () => {
    navigate("/edit-agent-profile", { state: { agent } });
  };

  console.log('Using Agent ID:', agent.agent_id);

  return (
    <div className="member-details-layout">
      <SideBar />
      <div className="member-details-main">
        <DetailsHeaderBar
          title="Retail Business Agent Details"
          subtitle="You can edit your details to update your profile"
          breadcrumbs={[
            { label: "Home", path: "/dashboard" },
            { label: "Manage Retail Advertising Business Agent", path: "/manage-agent" },
            { label: "Invitation History" }
          ]}
        />
        
        <DetailsTopBar
          tabs={tabs}
          activeTab="invitation"
          agent={agent}
        />

        {/* Error Message */}
        {error && (
          <div style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {error}
            <button 
              onClick={fetchReferralHistory} 
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        <HistoryTable
          columns={columns}
          data={tableData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={10}
          loading={loading} // Pass loading state to HistoryTable
        />
      </div>
    </div>
  );
}

export default AgentInvitationHistory;